import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, CalendarDays, AlertTriangle, FileText, GripVertical } from "lucide-react";
import { PROJECT_STATUSES, STATUS_LABELS, formatDate } from "../lib/constants";
const COLUMN_ORDER = [...PROJECT_STATUSES.map((s) => s.key), "on_hold"];

const COLUMN_ACCENT = {
  pending: "var(--st-pending-fg)",
  in_design: "var(--st-in-design-fg)",
  in_for_planning: "var(--st-planning-fg)",
  in_for_building_control: "var(--st-bc-fg)",
  under_construction: "var(--st-construction-fg)",
  complete: "var(--st-complete-fg)",
  on_hold: "var(--st-on-hold-fg)",
};

function dueBadge(due) {
  if (!due) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(`${due}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const diff = Math.round((d - today) / 86400000);
  if (diff < 0) return { text: `${Math.abs(diff)}d overdue`, overdue: true };
  if (diff === 0) return { text: "Due today", overdue: false };
  if (diff <= 14) return { text: `Due in ${diff}d`, overdue: false };
  return { text: formatDate(due), overdue: false };
}

function ageBar(project) {
  if (!project.status_changed_at) return null;
  const days = Math.round((Date.now() - new Date(project.status_changed_at).getTime()) / 86400000);
  if (days <= 7)  return { color: "var(--age-fresh)",  days };
  if (days <= 30) return { color: "var(--age-warm)",   days };
  if (days <= 90) return { color: "var(--age-warmer)", days };
  return { color: "var(--age-stale)", days };
}

function KanbanCard({ project, onDragStart, onDragEnd }) {
  const due = dueBadge(project.due_date);
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, project.id, project.current_status)}
      onDragEnd={onDragEnd}
      className="group cursor-grab active:cursor-grabbing"
      data-testid={`kanban-card-${project.id}`}
    >
      <Link
        to={`/admin/projects/${project.id}`}
        draggable={false}
        className="block card-surface p-4 hover-lift"
      >
        <div className="flex items-start gap-2 mb-1">
          <GripVertical
            size={12}
            className="mt-0.5 shrink-0 text-[color:var(--atech-text-muted)] opacity-0 group-hover:opacity-100 transition-opacity"
          />
          <div className="min-w-0 flex-1">
            <div className="label text-[color:var(--atech-text-muted)] truncate">
              {project.client_name}
            </div>
          </div>
        </div>
        <h4 className="font-display text-[15px] text-white leading-tight mb-2 group-hover:text-[color:var(--atech-copper)] transition-colors line-clamp-2 pl-[20px]">
          {project.project_name}
        </h4>

        {project.address && (
          <div className="flex items-center gap-1.5 text-[11px] text-[color:var(--atech-text-dim)] mb-2 pl-[20px]">
            <MapPin size={10} className="shrink-0" />
            <span className="truncate">{project.address}</span>
          </div>
        )}

        <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-white/5 pl-[20px]">
          <span className="font-mono text-[10px] text-[color:var(--atech-text-muted)]">
            {project.project_reference}
          </span>
          {due ? (
            <span
              className={`flex items-center gap-1 text-[10px] ${
                due.overdue ? "text-[#F87171]" : "text-[color:var(--atech-text-dim)]"
              }`}
            >
              {due.overdue ? <AlertTriangle size={10} /> : <CalendarDays size={10} />}
              {due.text}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-[10px] text-[color:var(--atech-text-muted)]">
              <FileText size={10} />
              {project.document_count || 0}
            </span>
          )}
        </div>
        {/* Age indicator bar */}
        {ageBar(project) && (
          <div
            className="mt-3 h-[2px] w-full rounded-full"
            style={{ background: ageBar(project).color }}
            title={`In ${STATUS_LABELS[project.current_status] || project.current_status} for ${ageBar(project).days} day${ageBar(project).days === 1 ? "" : "s"}`}
            data-testid={`age-bar-${project.id}`}
          />
        )}
      </Link>
    </div>
  );
}

export function KanbanBoard({ projects, onStatusChange }) {
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [draggingId, setDraggingId] = useState(null);

  const grouped = COLUMN_ORDER.reduce((acc, key) => {
    acc[key] = projects.filter((p) => p.current_status === key);
    return acc;
  }, {});

  const handleDragStart = (e, projectId, fromStatus) => {
    e.dataTransfer.setData("projectId", projectId);
    e.dataTransfer.setData("fromStatus", fromStatus);
    e.dataTransfer.effectAllowed = "move";
    setDraggingId(projectId);
  };

  const handleDragOver = (e, columnKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverColumn(columnKey);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverColumn(null);
    }
  };

  const handleDrop = (e, targetStatus) => {
    e.preventDefault();
    const projectId = e.dataTransfer.getData("projectId");
    const fromStatus = e.dataTransfer.getData("fromStatus");
    setDragOverColumn(null);
    setDraggingId(null);
    if (projectId && targetStatus !== fromStatus) {
      onStatusChange(projectId, targetStatus);
    }
  };

  const handleDragEnd = () => {
    setDragOverColumn(null);
    setDraggingId(null);
  };

  return (
    <div className="overflow-x-auto pb-4 -mx-1 px-1">
      <div className="flex gap-4" style={{ minWidth: "max-content" }}>
        {COLUMN_ORDER.map((statusKey) => {
          const items = grouped[statusKey] || [];
          const accent = COLUMN_ACCENT[statusKey];
          const label = STATUS_LABELS[statusKey] || statusKey;
          const isOver = dragOverColumn === statusKey;

          return (
            <div
              key={statusKey}
              className="flex flex-col"
              style={{ width: 272 }}
              data-testid={`kanban-column-${statusKey}`}
              onDragOver={(e) => handleDragOver(e, statusKey)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, statusKey)}
            >
              {/* Column header */}
              <div
                className="card-surface px-4 py-3 mb-3 flex items-center justify-between shrink-0"
                style={{ borderTop: `2px solid ${accent}` }}
              >
                <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[color:var(--atech-text)]">
                  {label}
                </span>
                <span
                  className="text-[10px] font-mono rounded-full px-2 py-0.5 font-semibold"
                  style={{
                    background: `color-mix(in srgb, ${accent} 14%, transparent)`,
                    color: accent,
                  }}
                >
                  {items.length}
                </span>
              </div>

              {/* Drop zone */}
              <div
                className="flex flex-col gap-2.5 flex-1 rounded-[2px] transition-all duration-150 min-h-[80px] p-1"
                style={
                  isOver
                    ? {
                        background: `color-mix(in srgb, ${accent} 7%, transparent)`,
                        outline: `1px dashed color-mix(in srgb, ${accent} 45%, transparent)`,
                        outlineOffset: "1px",
                      }
                    : {}
                }
              >
                {items.length === 0 ? (
                  <div
                    className={`py-10 text-center text-xs rounded-[2px] transition-colors ${
                      isOver
                        ? "text-[color:var(--atech-text-dim)]"
                        : "text-[color:var(--atech-text-muted)] border border-dashed border-white/8"
                    }`}
                  >
                    {isOver ? "Drop here" : "No projects"}
                  </div>
                ) : (
                  items.map((p) => (
                    <div
                      key={p.id}
                      style={{
                        opacity: draggingId === p.id ? 0.35 : 1,
                        transition: "opacity 150ms",
                      }}
                    >
                      <KanbanCard
                        project={p}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                      />
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default KanbanBoard;
